import { Box, CircularProgress } from '@mui/material';
import { isEmpty } from 'lodash';
import { useState } from 'react';
import { SearchButton, SearchInputWrapper } from './styled';
import DropdownSelect from '../../components/DropdownSelect';
import { SnackbarSeverityType } from '../../enums';
import ToastMessage from '../../components/ToastMessage';
import { Filters } from '../../apis/rollNoUpdate';
import { fetchAllStudentRollNo } from './utils/studentRollNoUtils';
import { RollNoData } from './types/rollNoDataType';
import { useSchoolClassesConfigHook } from '../common/hooks/useSchoolClassesConfig';
import RollNumberUpdateTable from './components/RollNumberUpdateTable';
import Icons from '../../components/icons';
import { PageWrapper, Title } from '../common/styled';
import { ButtonContainer } from '../Staff/styled';
import GenerateRollNoModal from './components/GenerateRollNoModal';
import { SCHOOLS_FOR_ROLL_NO_GENERATION } from './config/constants';

const RollNoUpdatePage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  const [grade, setGrade] = useState<string>('');
  const [division, setDivision] = useState<string>('');
  const [rollNoData, setRollNoData] = useState<RollNoData[]>([]);
  const [isGenerateRollNoModalOpen, setIsGenerateRollNoModalOpen] =
    useState(false);

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );

  const { gradeOptions, divisionOptions, loggedInUser } =
    useSchoolClassesConfigHook();

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const fetchStudentRollNoData = async () => {
    const filters: Filters = {
      selectedColumns: [
        'student__profile',
        'student__name',
        'student__enrollmentNo',
        'student__status',
        'student__rollNo',
        'student__email',
        'student__gender'
      ],
      grade,
      division
    };
    const response = await fetchAllStudentRollNo(filters);
    setRollNoData(response);
  };

  const handleSearch = async ({ displayToast }: { displayToast: boolean }) => {
    setIsSubmitting(true);
    setIsSearched(false);
    setRollNoData([]);
    try {
      await fetchStudentRollNoData();
      if (displayToast) {
        showToast('Successfully fetched Roll No', SnackbarSeverityType.success);
      }
    } catch (error: any) {
      showToast(
        error.errors?.[0].message || 'Failed to fetch student roll numbers.',
        SnackbarSeverityType.error
      );
    } finally {
      setIsSubmitting(false);
      setIsSearched(true);
    }
  };

  return (
    <>
      <Title>Roll No Update</Title>
      <Box>
        <PageWrapper sx={{ minHeight: 'auto' }}>
          <SearchInputWrapper>
            <DropdownSelect
              label='Grade'
              name='grade'
              value={grade}
              options={gradeOptions}
              placeholder='Select Grade'
              onChange={(event) => {
                setGrade(event.target.value as string);
                setIsSearched(false);
              }}
              hasTopLabel
            />
            <DropdownSelect
              label='Division'
              name='division'
              value={division}
              options={divisionOptions}
              placeholder='Select Division'
              onChange={(event) => {
                setDivision(event.target.value as string);
                setIsSearched(false);
              }}
              disabled={!grade}
              hasTopLabel
            />
            <ButtonContainer>
              <SearchButton
                onClick={() => handleSearch({ displayToast: true })}
                disabled={isSubmitting || !grade || !division}
              >
                <Icons.SearchWhiteOutlined style={{ marginRight: '6px' }} />
                {isSubmitting ? (
                  <CircularProgress size={24} color='inherit' />
                ) : (
                  'Search'
                )}
              </SearchButton>
              {isSearched &&
                !isEmpty(rollNoData) &&
                SCHOOLS_FOR_ROLL_NO_GENERATION.includes(
                  loggedInUser?.school || ''
                ) && (
                  <SearchButton
                    onClick={() => setIsGenerateRollNoModalOpen(true)}
                    disabled={isSubmitting}
                    sx={{ ml: 2 }}
                  >
                    Generate Roll Number
                  </SearchButton>
                )}
            </ButtonContainer>
          </SearchInputWrapper>
        </PageWrapper>
        {isSearched && (
          <RollNumberUpdateTable
            rollNoData={rollNoData}
            setRollNoData={setRollNoData}
            isSubmitting={isSubmitting}
            showToast={showToast}
            onRollNoUpdateSuccess={() => handleSearch({ displayToast: false })}
            handleSearch={handleSearch}
          />
        )}
      </Box>
      <GenerateRollNoModal
        open={isGenerateRollNoModalOpen}
        onClose={() => setIsGenerateRollNoModalOpen(false)}
        data={rollNoData}
        onGenerate={(updatedData) => {
          setRollNoData(updatedData);
        }}
      />
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={() => setToastOpen(false)}
      />
    </>
  );
};

export default RollNoUpdatePage;
